import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Plus, Trash2, FileDown } from 'lucide-react';
import { api } from '../../core/apiClient';
import { Input } from '../../components/ui/input';
import DocumentHeader from '../../components/DocumentHeader';
import StatusSelect from '../../components/StatusSelect';

interface Client { id: string; name: string; }
interface Project { id: string; name: string; projectNumber?: string; expedientNumber?: number; }
interface BudgetOption { id: string; budgetNumber: string; projecte?: string; }
interface UnitType { code: string; name: string; description: string; }
interface Node { id: string; name: string; code?: string; cachedTotalMaterialsCost: number; cachedTotalHoursOwn: number; cachedTotalHoursSubcontract: number; cachedTotalCost: number; cachedTotalWeightKg: number; preuVenda?: number; }

// Mateixa categorització que fa servir Escandalls/Pressupostos (LABOR_CATEGORY_KEYWORDS
// a NodeForm.tsx / LABOR_HOUR_FIELD_KEYWORDS a BudgetForm.tsx) per repartir les
// hores de mà d'obra entre H.O/H.F/H.P/H.M/H.E en vincular un escandall.
const LABOR_HOUR_FIELD_KEYWORDS: { pattern: RegExp; field: 'horesO' | 'horesF' | 'horesP' | 'horesM' | 'horesE' }[] = [
  { pattern: /oficina/i,       field: 'horesO' },
  { pattern: /extern/i,        field: 'horesE' },
  { pattern: /muntatge/i,      field: 'horesM' },
  { pattern: /fabricaci[oó]/i, field: 'horesF' },
  { pattern: /producci[oó]/i,  field: 'horesP' },
];
function laborHourField(description: string): 'horesO' | 'horesF' | 'horesP' | 'horesM' | 'horesE' {
  return LABOR_HOUR_FIELD_KEYWORDS.find(({ pattern }) => pattern.test(description))?.field ?? 'horesP';
}

// Mateixa estructura de línia que budget_lines, per poder mostrar/editar
// exactament les mateixes columnes que el detall del Pressupost.
interface SOLine {
  lineNumber: number;
  description: string;
  longDescription?: string;
  unitOfMeasure: string;
  llarg: number;
  ample: number;
  alt: number;
  quantity: number;      // Amidament (= medició del pressupost)
  tipus: string;         // Tipus d'unitat (Ut/Kg/M2...), catàleg /nodes/unit-types
  nodeId?: string;
  nodeName?: string;
  costMaterials: number;
  costServeis: number;
  costIndirecte: number;
  horesP: number;
  horesF: number;
  horesM: number;
  horesO: number;
  horesE: number;
  totalHores: number;
  costTotal: number;
  preuUA: number;
  unitPrice: number;     // Preu UT (= Amidament x Preu UA)
  discountPercent: number;
  lineTotal: number;     // Import
  pes: number;
  factor: number;
}

interface SOForm {
  orderDate: string;
  validityDate: string;
  expectedDeliveryDate: string;
  status: string;
  clientId: string;
  projectId: string;
  budgetId: string;
  discountPercent: number;
  taxRate: number;
  currency: string;
  paymentDays: number;
  paymentConditions: string;
  deliveryConditions: string;
  notes: string;
  lines: SOLine[];
}

const STATUSES = ['draft', 'accepted', 'delivered', 'invoiced', 'cancelled'];
const STATUS_LABEL: Record<string, string> = {
  draft:     'Esborrany',
  accepted:  'Acceptat',
  delivered: 'Lliurat',
  invoiced:  'Facturat',
  cancelled: 'Cancel·lat',
};
const STATUS_CLASS: Record<string, string> = {
  draft:     'bg-gray-100 text-gray-700',
  accepted:  'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  invoiced:  'bg-purple-100 text-purple-700',
  cancelled: 'bg-red-100 text-red-700',
};

const EMPTY_LINE = (n: number): SOLine => ({
  lineNumber: n, description: '', unitOfMeasure: 'Ut',
  llarg: 0, ample: 0, alt: 0, quantity: 0, tipus: 'Ut',
  costMaterials: 0, costServeis: 0, costIndirecte: 0,
  horesP: 0, horesF: 0, horesM: 0, horesO: 0, horesE: 0, totalHores: 0, costTotal: 0,
  preuUA: 0, unitPrice: 0, discountPercent: 0, lineTotal: 0, pes: 0, factor: 1,
});

const EMPTY_FORM = (): SOForm => ({
  orderDate: new Date().toISOString().split('T')[0],
  validityDate: '',
  expectedDeliveryDate: '',
  status: 'draft',
  clientId: '',
  projectId: '',
  budgetId: '',
  discountPercent: 0,
  taxRate: 21,
  currency: 'EUR',
  paymentDays: 30,
  paymentConditions: '',
  deliveryConditions: '',
  notes: '',
  lines: [],
});

const budgetLineToSOLine = (bl: any, lineNumber: number): SOLine =>
  calcLine({
    lineNumber,
    description:     bl.concepte || '(sense descripció)',
    longDescription: bl.descripcioLlarga || undefined,
    unitOfMeasure:   bl.unitat || 'Ut',
    llarg:           Number(bl.llarg ?? 0),
    ample:           Number(bl.ample ?? 0),
    alt:             Number(bl.alt ?? 0),
    quantity:        Number(bl.medicio ?? 1) || 1,
    tipus:           bl.tipus || 'Ut',
    nodeId:          bl.nodeId || undefined,
    nodeName:        bl.nodeName || undefined,
    costMaterials:   Number(bl.costMaterials ?? 0),
    costServeis:     Number(bl.costServeis ?? 0),
    costIndirecte:   Number(bl.costIndirecte ?? 0),
    horesP:          Number(bl.horesP ?? 0),
    horesF:          Number(bl.horesF ?? 0),
    horesM:          Number(bl.horesM ?? 0),
    horesO:          Number(bl.horesO ?? 0),
    horesE:          Number(bl.horesE ?? 0),
    totalHores:      0,
    costTotal:       Number(bl.totalCost ?? 0),
    preuUA:          Number(bl.preuUA ?? 0),
    unitPrice:       0,
    discountPercent: Number(bl.descompte ?? 0),
    lineTotal:       0,
    pes:             Number(bl.pes ?? 0),
    factor:          Number(bl.factor ?? 1),
  });

// Mateixa lògica que budget_lines: l'Amidament es recalcula de les dimensions
// si s'han informat, Preu UT = Amidament × Preu UA, i l'Import surt del Preu UT
// (que ja inclou l'Amidament) menys el descompte — no es torna a multiplicar.
const round2 = (v: number) => Math.round((Number(v) || 0) * 100) / 100;

function calcLine(l: SOLine): SOLine {
  const dimsQty = (l.llarg || 0) * (l.ample || 0) * (l.alt || 0);
  const quantity = dimsQty > 0 ? dimsQty : l.quantity;
  const unitPrice = round2(quantity * (l.preuUA || 0));
  const lineTotal = round2(unitPrice * (1 - (l.discountPercent || 0) / 100));
  const horesP = l.horesP || 0, horesF = l.horesF || 0, horesM = l.horesM || 0, horesO = l.horesO || 0, horesE = l.horesE || 0;
  return { ...l, quantity, unitPrice, lineTotal, totalHores: round2((horesP + horesF + horesM + horesO + horesE) * quantity) };
}

const fmt2 = (v: number) =>
  Number(v || 0).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SalesOrderForm() {
  const navigate  = useNavigate();
  const { id }    = useParams<{ id: string }>();
  const isNew     = !id || id === 'new';

  const [form, setForm]           = useState<SOForm>(EMPTY_FORM());
  const [orderNumber, setOrderNumber] = useState('(auto)');
  const [loading, setLoading]     = useState(!isNew);
  const [saving, setSaving]       = useState(false);
  const [clients, setClients]     = useState<Client[]>([]);
  const [projects, setProjects]   = useState<Project[]>([]);
  const [clientBudgets, setClientBudgets] = useState<BudgetOption[]>([]);
  const [importingBudget, setImportingBudget] = useState(false);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [nodes, setNodes]         = useState<Node[]>([]);

  // Edicio de la taula Línies — mateix patró que el Detall del Pressupost:
  // inputs sempre visibles, editCells guarda el text en curs mentre s'edita
  // una cel·la numèrica per no perdre la coma decimal fins que es fa blur.
  const [editCells, setEditCells] = useState<Record<string, string>>({});
  const [conceptOpenIdx, setConceptOpenIdx] = useState<number | null>(null);
  const [nodeSearch, setNodeSearch]     = useState('');
  const [nodePopupRow, setNodePopupRow] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [clientsRes, projRes, unitTypesRes, nodesRes] = await Promise.all([
          api.get<any>('/clients', { limit: 500, isActive: 1 }),
          api.get<any>('/taller/projects', { limit: 500 }),
          api.get<any>('/nodes/unit-types').catch(() => ({ data: [] })),
          api.get<any>('/nodes', { limit: 500, isActive: 1 }).catch(() => ({ data: [] })),
        ]);
        setClients(clientsRes.data ?? []);
        setProjects(projRes.data ?? []);
        setUnitTypes(unitTypesRes.data ?? []);
        setNodes(nodesRes.data ?? []);

        if (!isNew && id) {
          const res = await api.get<any>(`/sales-orders/${id}`);
          const so = res.data;
          setOrderNumber(so.orderNumber);
          setForm({
            orderDate:            so.orderDate?.split('T')[0] ?? '',
            validityDate:         so.validityDate?.split('T')[0] ?? '',
            expectedDeliveryDate: so.expectedDeliveryDate?.split('T')[0] ?? '',
            status:             so.status ?? 'draft',
            clientId:           so.clientId ?? '',
            projectId:          so.projectId ?? '',
            budgetId:           so.budgetId ?? '',
            discountPercent:    Number(so.discountPercent ?? 0),
            taxRate:            Number(so.taxRate ?? 21),
            currency:           so.currency ?? 'EUR',
            paymentDays:        Number(so.paymentDays ?? 30),
            paymentConditions:  so.paymentConditions ?? '',
            deliveryConditions: so.deliveryConditions ?? '',
            notes:              so.notes ?? '',
            lines: (so.lines ?? []).map((l: any) => calcLine({
              lineNumber:      l.lineNumber,
              description:     l.description ?? '',
              longDescription: l.longDescription ?? undefined,
              unitOfMeasure:   l.unitOfMeasure ?? 'Ut',
              llarg:           Number(l.llarg ?? 0),
              ample:           Number(l.ample ?? 0),
              alt:             Number(l.alt ?? 0),
              quantity:        Number(l.quantity ?? 1),
              tipus:           l.tipus ?? 'Ut',
              nodeId:          l.nodeId ?? undefined,
              costMaterials:   Number(l.costMaterials ?? 0),
              costServeis:     Number(l.costServeis ?? 0),
              costIndirecte:   Number(l.costIndirecte ?? 0),
              horesP:          Number(l.horesP ?? 0),
              horesF:          Number(l.horesF ?? 0),
              horesM:          Number(l.horesM ?? 0),
              horesO:          Number(l.horesO ?? 0),
              horesE:          Number(l.horesE ?? 0),
              totalHores:      0,
              costTotal:       Number(l.costTotal ?? 0),
              preuUA:          Number(l.preuUA ?? 0),
              unitPrice:       Number(l.unitPrice ?? 0),
              discountPercent: Number(l.discountPercent ?? 0),
              lineTotal:       Number(l.lineTotal ?? 0),
              pes:             Number(l.pes ?? 0),
              factor:          Number(l.factor ?? 1),
            })),
          });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isNew]);

  const fetchClientBudgets = useCallback(async (clientId: string) => {
    if (!clientId) { setClientBudgets([]); return; }
    try {
      const res = await api.get<any>('/budgets', { clientId, limit: 200 });
      setClientBudgets(res.data ?? []);
    } catch {
      setClientBudgets([]);
    }
  }, []);

  useEffect(() => { fetchClientBudgets(form.clientId); }, [form.clientId, fetchClientBudgets]);

  const importBudgetLines = async () => {
    if (!form.budgetId) return;
    if (form.lines.length > 0 && !confirm('Ja hi ha línies a la comanda. Vols reemplaçar-les per les del pressupost seleccionat?')) return;
    setImportingBudget(true);
    try {
      const res = await api.get<any>(`/budgets/${form.budgetId}`);
      const budgetLines: any[] = res.data?.lines ?? [];
      const newLines = budgetLines.map((bl, idx) => budgetLineToSOLine(bl, idx + 1));
      setForm((f) => ({ ...f, lines: newLines }));
    } catch (err: any) {
      alert('Error important el pressupost: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setImportingBudget(false);
    }
  };

  const addLine = () =>
    setForm((f) => ({ ...f, lines: [...f.lines, EMPTY_LINE(f.lines.length + 1)] }));

  const removeLine = (i: number) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.filter((_, idx) => idx !== i).map((l, idx) => ({ ...l, lineNumber: idx + 1 })),
    }));

  const updateLine = (i: number, field: keyof SOLine, value: unknown) =>
    setForm((f) => {
      const lines = [...f.lines];
      lines[i] = calcLine({ ...lines[i], [field]: value } as SOLine);
      return { ...f, lines };
    });

  const numCellInput = (ri: number, col: keyof SOLine) => {
    const key = `${ri}_${String(col)}`;
    const stored = editCells[key];
    const numVal = Number(form.lines[ri][col] ?? 0);
    return (
      <Input
        type="text" inputMode="decimal"
        value={stored !== undefined ? stored : (numVal === 0 ? '' : fmt2(numVal))}
        onFocus={() => setEditCells((p) => ({ ...p, [key]: numVal === 0 ? '' : String(numVal).replace('.', ',') }))}
        onChange={(e) => setEditCells((p) => ({ ...p, [key]: e.target.value }))}
        onBlur={() => {
          const parsed = parseFloat((editCells[key] ?? '').replace(',', '.')) || 0;
          updateLine(ri, col, parsed);
          setEditCells((p) => { const n = { ...p }; delete n[key]; return n; });
        }}
        placeholder="0"
        className="w-full h-5 text-right" style={{ fontSize: '9px', padding: '0 2px' }}
      />
    );
  };

  const importNode = async (rowIdx: number, node: Node) => {
    // Mateixa lògica que importNode a BudgetForm.tsx: reparteix les hores
    // pròpies/subcontractades del node entre H.O/H.F/H.P/H.M/H.E llegint les
    // seves línies de mà d'obra directes (nature MO_PROPIA/MO_SUBCONTRACT).
    const hours: Record<'horesO' | 'horesF' | 'horesP' | 'horesM' | 'horesE', number> = {
      horesO: 0, horesF: 0, horesP: 0, horesM: 0, horesE: 0,
    };
    let costServeis = 0;
    let costIndirecte = 0;
    try {
      const res = await api.get<{ success: boolean; data: { lines?: any[]; cachedTotalServicesCost?: number; cachedTotalIndirectCost?: number } }>(`/nodes/${node.id}`);
      const nodeLines: any[] = res.data?.lines ?? [];
      let directHours = 0;
      nodeLines
        .filter((l) => l.nature === 'MO_PROPIA' || l.nature === 'MO_SUBCONTRACT')
        .forEach((l) => {
          const h = Number(l.measurement || 0);
          hours[laborHourField(l.description ?? '')] += h;
          directHours += h;
        });
      const totalHoresNode = Number(node.cachedTotalHoursOwn ?? 0) + Number(node.cachedTotalHoursSubcontract ?? 0);
      const hiddenHours = totalHoresNode - directHours;
      if (hiddenHours > 0.005) hours.horesP += hiddenHours;
      costServeis   = Number(res.data?.cachedTotalServicesCost ?? 0);
      costIndirecte = Number(res.data?.cachedTotalIndirectCost ?? 0);
    } catch {
      hours.horesP = Number(node.cachedTotalHoursOwn ?? 0) + Number(node.cachedTotalHoursSubcontract ?? 0);
    }

    setForm((f) => {
      const lines = [...f.lines];
      lines[rowIdx] = calcLine({
        ...lines[rowIdx],
        nodeId: node.id,
        nodeName: node.name,
        description: lines[rowIdx].description || node.name,
        quantity: lines[rowIdx].quantity || 1,
        costMaterials: Number(node.cachedTotalMaterialsCost ?? 0),
        costServeis,
        costIndirecte,
        ...hours,
        costTotal: Number(node.cachedTotalCost ?? 0),
        pes: Number(node.cachedTotalWeightKg ?? 0),
        preuUA: Number(node.preuVenda ?? 0),
      });
      return { ...f, lines };
    });
    setNodePopupRow(null);
    setNodeSearch('');
  };

  const subtotal = round2(form.lines.reduce((s, l) => s + l.lineTotal, 0));
  const discountAmount = round2(subtotal * (form.discountPercent / 100));
  const taxable = round2(subtotal - discountAmount);
  const taxAmount = round2(taxable * (form.taxRate / 100));
  const total = round2(taxable + taxAmount);

  const handleSave = async () => {
    if (!form.clientId) { alert('Cal seleccionar un client'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        validityDate:         form.validityDate         || null,
        expectedDeliveryDate: form.expectedDeliveryDate || null,
        projectId:            form.projectId            || null,
        budgetId:             form.budgetId             || null,
        lines: form.lines,
      };
      if (isNew) {
        await api.post<any>('/sales-orders', payload);
      } else {
        await api.put(`/sales-orders/${id}`, payload);
      }
      sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('comandes'));
      navigate('/crm');
    } catch (err: any) {
      alert('Error desant: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelCls = 'block text-xs font-medium text-muted-foreground mb-1';

  const filteredNodes = nodes.filter((n) =>
    !nodeSearch || n.name.toLowerCase().includes(nodeSearch.toLowerCase()) || (n.code ?? '').toLowerCase().includes(nodeSearch.toLowerCase()),
  );

  if (loading) return <div className="p-8 text-muted-foreground text-xs">Carregant...</div>;

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <DocumentHeader
        title={isNew ? 'Nova comanda de client' : orderNumber}
        status={!isNew && (
          <StatusSelect
            value={form.status}
            options={STATUSES.map((s) => ({ code: s, name: STATUS_LABEL[s], className: STATUS_CLASS[s] }))}
            fallbackLabel={form.status}
            onChange={(code) => setForm((f) => ({ ...f, status: code }))}
          />
        )}
        actions={<>
          <button
            onClick={() => { sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('comandes')); navigate('/crm'); }}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
          >
            Cancel·lar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <Save size={14} /> {saving ? 'Desant...' : 'Desar'}
          </button>
        </>}
      />

      {/* Contingut */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">

        {/* Capçalera */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Capçalera</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>Número</label>
              <input className={inputCls} value={orderNumber} disabled style={{ backgroundColor: '#f9fafb' }} />
            </div>
            <div>
              <label className={labelCls}>Data *</label>
              <input type="date" className={inputCls} value={form.orderDate}
                onChange={(e) => setForm((f) => ({ ...f, orderDate: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Validesa fins</label>
              <input type="date" className={inputCls} value={form.validityDate}
                onChange={(e) => setForm((f) => ({ ...f, validityDate: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Lliurament previst</label>
              <input type="date" className={inputCls} value={form.expectedDeliveryDate}
                onChange={(e) => setForm((f) => ({ ...f, expectedDeliveryDate: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Client *</label>
              <select className={inputCls} value={form.clientId}
                onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}>
                <option value="">— Selecciona client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Expedient relacionat</label>
              <select className={inputCls} value={form.projectId}
                onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}>
                <option value="">— Sense expedient —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.expedientNumber ? `EXP-${p.expedientNumber}` : ''} {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>% Descompte global</label>
              <input type="number" min="0" max="100" step="0.01" className={inputCls}
                value={form.discountPercent}
                onChange={(e) => setForm((f) => ({ ...f, discountPercent: Number(e.target.value) }))} />
            </div>
            <div>
              <label className={labelCls}>% IVA</label>
              <input type="number" min="0" max="100" step="0.1" className={inputCls}
                value={form.taxRate}
                onChange={(e) => setForm((f) => ({ ...f, taxRate: Number(e.target.value) }))} />
            </div>
            <div>
              <label className={labelCls}>Dies de pagament</label>
              <input type="number" min="0" className={inputCls}
                value={form.paymentDays}
                onChange={(e) => setForm((f) => ({ ...f, paymentDays: Number(e.target.value) }))} />
            </div>
            <div>
              <label className={labelCls}>Moneda</label>
              <select className={inputCls} value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}>
                <option value="EUR">EUR €</option>
                <option value="USD">USD $</option>
                <option value="GBP">GBP £</option>
              </select>
            </div>
          </div>
        </div>

        {/* Línies — mateix format que el Detall del Pressupost (BudgetForm.tsx) */}
        <div className="bg-muted/30 border rounded">
          <div className="w-full bg-slate-100 px-4 py-2 font-medium flex justify-between items-center gap-3 text-[11px] rounded-t">
            <span>Línies</span>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                value={form.budgetId}
                disabled={!form.clientId || clientBudgets.length === 0}
                onChange={(e) => setForm((f) => ({ ...f, budgetId: e.target.value }))}
              >
                <option value="">
                  {!form.clientId ? '— Selecciona primer un client —' : clientBudgets.length === 0 ? 'Sense pressupostos per aquest client' : '— Selecciona pressupost —'}
                </option>
                {clientBudgets.map((b) => (
                  <option key={b.id} value={b.id}>{b.budgetNumber}{b.projecte ? ` — ${b.projecte}` : ''}</option>
                ))}
              </select>
              <button onClick={importBudgetLines} disabled={!form.budgetId || importingBudget}
                className="flex items-center gap-1 px-3 py-1.5 border border-blue-200 text-blue-700 bg-blue-50 rounded text-xs hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Importa totes les línies del pressupost seleccionat">
                <FileDown size={13} /> {importingBudget ? 'Important...' : 'Importar pressupost'}
              </button>
              <button onClick={addLine}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white rounded text-xs hover:bg-gray-700">
                <Plus size={13} /> Afegir línia
              </button>
            </div>
          </div>

          {form.lines.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">Cap línia. Afegeix-ne una o importa un pressupost.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="escandall-lines-table w-full" style={{ fontSize: '9px', lineHeight: '1.2' }}>
                <thead className="bg-slate-200">
                  <tr>
                    {[
                      { h: '#', align: 'text-left' },
                      { h: 'Concepte', align: 'text-left' },
                      { h: 'Amidament', align: 'text-right' },
                      { h: 'Tipus', align: 'text-left' },
                      { h: 'Llarg', align: 'text-right' },
                      { h: 'Ample', align: 'text-right' },
                      { h: 'Alt', align: 'text-right' },
                      { h: 'Cost Mat.', align: 'text-right' },
                      { h: 'C.Serveis', align: 'text-right' },
                      { h: 'C.Indirecte', align: 'text-right' },
                      { h: 'H.O', align: 'text-right' },
                      { h: 'H.F', align: 'text-right' },
                      { h: 'H.P', align: 'text-right' },
                      { h: 'H.M', align: 'text-right' },
                      { h: 'H.E', align: 'text-right' },
                      { h: 'T.Hores', align: 'text-right' },
                      { h: 'T.Cost', align: 'text-right' },
                      { h: 'Preu UA', align: 'text-right' },
                      { h: 'Dte%', align: 'text-right' },
                      { h: 'Import', align: 'text-right' },
                      { h: 'F.Marge', align: 'text-right' },
                      { h: 'Pes', align: 'text-right' },
                      { h: 'Factor', align: 'text-right' },
                      { h: '', align: 'text-left' },
                    ].map((c, i) => (
                      <th key={i} className={`px-0.5 py-0.5 ${c.align} font-medium whitespace-nowrap`} style={{ fontSize: '9px' }}>{c.h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((l, ri) => {
                    const concepteSuggestions = conceptOpenIdx === ri && l.description.length >= 1
                      ? nodes.filter((n) =>
                          n.name.toLowerCase().includes(l.description.toLowerCase()) ||
                          (n.code ?? '').toLowerCase().includes(l.description.toLowerCase())
                        ).slice(0, 8)
                      : [];
                    const lineFactorMarge = Number(l.costTotal) > 0 ? Number(l.lineTotal) / Number(l.costTotal) : 0;

                    return (
                      <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-0.5 py-0.5 text-muted-foreground" style={{ fontSize: '9px' }}>{ri + 1}</td>
                        <td className="px-0.5 py-0.5 min-w-[140px] relative">
                          <div className="flex items-center gap-0.5">
                            <Input
                              value={l.description}
                              onChange={(e) => { updateLine(ri, 'description', e.target.value); setConceptOpenIdx(ri); }}
                              onFocus={() => setConceptOpenIdx(ri)}
                              onBlur={() => setTimeout(() => setConceptOpenIdx((v) => (v === ri ? null : v)), 150)}
                              placeholder="Cercar escandall o escriure text lliure..."
                              className="w-full h-5" style={{ fontSize: '9px', padding: '0 4px' }}
                            />
                            <button
                              type="button"
                              title="Vincular Escandall"
                              onMouseDown={(e) => { e.preventDefault(); setNodePopupRow(ri); setNodeSearch(''); }}
                              className="shrink-0 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                            >
                              ⬡
                            </button>
                          </div>
                          {concepteSuggestions.length > 0 && (
                            <ul className="absolute z-50 left-0 top-full mt-0.5 w-64 bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto text-xs">
                              {concepteSuggestions.map((n) => (
                                <li key={n.id}
                                  onMouseDown={(e) => { e.preventDefault(); importNode(ri, n); setConceptOpenIdx(null); }}
                                  className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0">
                                  <span className="font-medium">{n.name}</span>
                                  {n.code && <span className="text-gray-400 ml-1">({n.code})</span>}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-0.5 py-0.5 w-16">{numCellInput(ri, 'quantity')}</td>
                        <td className="px-0.5 py-0.5 w-24">
                          <select value={l.tipus} onChange={(e) => updateLine(ri, 'tipus', e.target.value)}
                            className="w-full h-5 border rounded bg-white" style={{ fontSize: '9px', padding: '0 2px' }}>
                            {l.tipus && !unitTypes.some((ut) => ut.code === l.tipus) && (
                              <option value={l.tipus}>{l.tipus}</option>
                            )}
                            {unitTypes.length > 0
                              ? unitTypes.map((ut) => <option key={ut.code} value={ut.code}>{ut.code}</option>)
                              : <option value="Ut">Ut</option>}
                          </select>
                        </td>
                        <td className="px-0.5 py-0.5 w-16">{numCellInput(ri, 'llarg')}</td>
                        <td className="px-0.5 py-0.5 w-16">{numCellInput(ri, 'ample')}</td>
                        <td className="px-0.5 py-0.5 w-16">{numCellInput(ri, 'alt')}</td>
                        <td className="px-0.5 py-0.5 w-20">{numCellInput(ri, 'costMaterials')}</td>
                        <td className="px-0.5 py-0.5 w-20">{numCellInput(ri, 'costServeis')}</td>
                        <td className="px-0.5 py-0.5 w-20">{numCellInput(ri, 'costIndirecte')}</td>
                        <td className="px-0.5 py-0.5 w-14">{numCellInput(ri, 'horesO')}</td>
                        <td className="px-0.5 py-0.5 w-14">{numCellInput(ri, 'horesF')}</td>
                        <td className="px-0.5 py-0.5 w-14">{numCellInput(ri, 'horesP')}</td>
                        <td className="px-0.5 py-0.5 w-14">{numCellInput(ri, 'horesM')}</td>
                        <td className="px-0.5 py-0.5 w-14">{numCellInput(ri, 'horesE')}</td>
                        <td className="px-0.5 py-0.5 w-16 text-muted-foreground text-right" style={{ fontSize: '9px' }}>{fmt2(l.totalHores)}</td>
                        <td className="px-0.5 py-0.5 w-20">{numCellInput(ri, 'costTotal')}</td>
                        <td className="px-0.5 py-0.5 w-20">{numCellInput(ri, 'preuUA')}</td>
                        <td className="px-0.5 py-0.5 w-14">{numCellInput(ri, 'discountPercent')}</td>
                        <td className="px-0.5 py-0.5 w-20 text-right font-medium text-foreground" style={{ fontSize: '9px' }}>{fmt2(l.lineTotal)} €</td>
                        <td className="px-0.5 py-0.5 w-16 text-right" style={{ fontSize: '9px' }}>{fmt2(lineFactorMarge)}</td>
                        <td className="px-0.5 py-0.5 w-16">{numCellInput(ri, 'pes')}</td>
                        <td className="px-0.5 py-0.5 w-16">{numCellInput(ri, 'factor')}</td>
                        <td className="px-1 py-0.5 w-8">
                          <button onClick={() => removeLine(ri)}
                            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600">
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-medium border-t-2 border-gray-300" style={{ fontSize: '9px' }}>
                    <td colSpan={19} className="px-2 py-1.5">TOTALS</td>
                    <td className="px-2 py-1.5 text-right font-bold">{fmt2(subtotal)} €</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Totals */}
          <div className="p-3 flex justify-end">
            <div className="w-64 space-y-1 text-xs">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span>{fmt2(subtotal)} €</span>
              </div>
              {form.discountPercent > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Descompte ({form.discountPercent}%)</span>
                  <span>-{fmt2(discountAmount)} €</span>
                </div>
              )}
              <div className="flex justify-between text-muted-foreground">
                <span>IVA ({form.taxRate}%)</span>
                <span>{fmt2(taxAmount)} €</span>
              </div>
              <div className="flex justify-between font-semibold text-sm border-t border-gray-200 pt-1 mt-1">
                <span>Total</span>
                <span>{fmt2(total)} €</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Notes</h2>
          <textarea
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Notes visibles al client..."
          />
        </div>

      </div>

      {/* ── Modal Node Popup ── */}
      {nodePopupRow !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setNodePopupRow(null)}>
          <div className="bg-white rounded-xl shadow-xl w-[480px] max-h-[60vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <h2 className="font-semibold text-foreground text-sm">Vincular Escandall</h2>
              <input autoFocus className="flex-1 px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Cercar per nom o codi..."
                value={nodeSearch} onChange={(e) => setNodeSearch(e.target.value)} />
            </div>
            <div className="overflow-y-auto flex-1">
              {filteredNodes.slice(0, 50).map((n) => (
                <button key={n.id} onClick={() => importNode(nodePopupRow, n)}
                  className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 text-xs">
                  <span className="font-medium text-foreground">{n.name}</span>
                  {n.code && <span className="ml-2 text-muted-foreground text-xs">{n.code}</span>}
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Cost {fmt2(n.cachedTotalCost)} € · {fmt2(n.cachedTotalHoursOwn + n.cachedTotalHoursSubcontract)} h · {fmt2(n.cachedTotalWeightKg)} kg
                  </div>
                </button>
              ))}
              {filteredNodes.length === 0 && (
                <p className="p-4 text-xs text-muted-foreground text-center">No s'han trobat escandalls</p>
              )}
            </div>
            <div className="p-3 border-t border-gray-100 flex justify-end">
              <button onClick={() => setNodePopupRow(null)} className="px-3 py-1.5 text-xs text-muted-foreground hover:bg-gray-100 rounded">
                Cancel·lar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
